import React, {useCallback, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import PrecursorForm from '../../components/PrecursorForm/PrecursorForm';
import useUpdatePrecursor, {
  useContactIdAndReviewFor,
} from '../../hooks/useUpdatePrecursor';
import {useAccountContext} from '../../contexts/AccountContext';
import {
  getCreateNewReviewFields,
  precursorAndHeaderFieldsType,
} from './fields/precursorFields';
import Loading from '../../components/Loading/Loading';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useAllPadAdvisers} from '../../hooks/useAllPadAdvisers';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import {useQueryParams} from '../../hooks/useQueryParams';
import {ContactAttribution, ReviewReason} from '../PrecursorSummaryPage/headerFields';
import {newUTCDate} from '../../utils/newUTCDate';
import {ErrorPage} from '../ErrorPage';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {useFeaturePermission} from '../../contexts/FeatureBoundary';
import {getPrecursorSubmissionRedirect} from '../HandoverSummaryPage/handoverNavigation';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';

export function PrecursorWizardPage() {
  return (
    <>
      <HandoverStep />
      <PrecursorWizard />
    </>
  );
}

export function PrecursorWizard() {
  const {reviewId} = useParams<{
    reviewId: string;
  }>();
  const params = useQueryParams();
  const {account} = useAccountContext();
  const {allPadAdvisers} = useAllPadAdvisers();

  const navigate = useNavigate();
  const canUseReportPack = useFeaturePermission([
    GlobalPermissionEnum.can_access_report_pack,
  ]);
  const {enqueueSnackbar} = useSnackbar();
  const {reviewDetails} = useReviewContext();
  const {values: valuesReview} = useAutoForm<precursorAndHeaderFieldsType>();
  const {reviewFor} = useContactIdAndReviewFor(valuesReview);
  const skipPrecursorInitialOverrides = useMemo(() => {
    if (params.get('skip') === 'true') {
      const initialValue: Partial<precursorAndHeaderFieldsType> = {
        reviewContact: ContactAttribution.both,
        isReviewFocused: 'No',
        meetingHappened: 'Yes',
        pastMeetingDate: newUTCDate('September 5, 2022'),
        meetingType: 'Web',
        opportunityValue: '0.00',
        step: 0,
        reviewFor,
        // otherVulnerability: 'No',
        // otherEventVulnerability: 'No',
        // specialCareReasons: '',
      };
      return initialValue;
    }
    return undefined;
  }, [params]);
  const onError = useCallback(
    () =>
      enqueueSnackbar('There was a problem with your answers. Please try again', {
        variant: 'error',
        autoHideDuration: null,
      }),
    [enqueueSnackbar]
  );

  const {updateReview} = useUpdatePrecursor({
    reviewId: reviewId!,
    onSuccess: (res) => {
      if (!res) {
        return;
      }

      // Resolve the next tubestop from the freshly-mutated review, not from the
      // cached ReviewContext that HandoverIndexRedirectPage reads — that cache
      // can still hold the pre-submit `precursorNotFinished: true` and circle
      // the adviser back to the start (ON-9166).
      const redirectPath = getPrecursorSubmissionRedirect({
        reviewDetails: res,
        canUseReportPack,
      });

      navigate({
        pathname: redirectPath,
      });
    },
    onError,
  });

  const fields = useMemo(
    () => getCreateNewReviewFields(reviewDetails, account, allPadAdvisers),
    [reviewDetails, account, allPadAdvisers]
  );
  if (!account) {
    return <Loading entityName="account" />;
  }
  if (!reviewId) {
    return <Loading entityName="review" />;
  }

  if (!fields || fields.length === 0) {
    return <ErrorPage text="No fields found" />;
  }

  const initialValueOverrides: Partial<precursorAndHeaderFieldsType> = {
    reviewContact: account?.secondaryContact
      ? ContactAttribution.both
      : account?.primaryContact?.id,
    step: 0,
    reviewReason: reviewDetails?.reviewReason as ReviewReason,
    previousCommunications:
      reviewDetails?.clientCommunicationAttempts?.map((attempt) => ({
        contactDate: attempt.contactDate,
        contactMethod: attempt.contactMethod,
      })) || [],
  };

  return (
    <>
      <PrecursorForm
        initialValueOverrides={skipPrecursorInitialOverrides || initialValueOverrides}
        submit={(values) => {
          return updateReview(
            {
              ...values,
            },
            {inPrecursor: true}
          );
        }}
        validationSchema={undefined}
        formFields={fields}
      />
    </>
  );
}
